import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useRecordingProtection } from '@/hooks/useRecordingProtection';
import { recordingStorage } from '@/services/recordingStorage';
import { appointmentWrapperService } from '@/services/appointmentWrapper';
import { appointmentService } from '@/services/appointment';
import { apiService } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { encounterService } from '@/services/encounter';
import { App } from '@capacitor/app';
import { PlatformService } from '@/utils/platform';
import TranscriptionProgress from '@/components/TranscriptionProgress';
import DebugAudioUploader from '@/components/DebugAudioUploader';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';
import ConnectedModeBadge from '@/components/ConnectedModeBadge';
import QuickRecordingModal from '@/components/QuickRecordingModal';
import { RecordingNotAllowed } from '@/components/recording/RecordingNotAllowed';
import RecordAccessDenied from '@/components/RecordAccessDenied';
import { isRecordAccessDenied } from '@/utils/recordAccess';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useOrganizationStore } from '@/stores/organizationStore';
import AudioSphere from '@/components/recording/AudioSphere';
import AudioSphere2D from '@/components/recording/AudioSphere2D';
import { NetworkStatusBadge, ProtectionStatusBadge } from '@/components/recording/NetworkStatusBadge';
import { RecordingRecoveryModal } from '@/components/recording/RecordingRecoveryModal';
import { useVisualizationMode } from '@/utils/webglDetect';
import { isSentinelDate, formatEhrTime, formatEhrDate, EHR_PRE_LOCALIZED_SYSTEMS } from '@/utils/dateFormat';
import { useRecordingSessionStore } from '@/stores/recordingSessionStore';
import { useUploadJobStore } from '@/stores/uploadJobStore';
import { reviewPathFor } from '@/utils/reviewPath';
import { isAlreadyRecorded } from '@/utils/encounterUrl';
import { selectPageJob } from '@/utils/selectPageJob';
import {
  MicrophoneIcon,
  StopIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  UserIcon,
  ClockIcon,
  MapPinIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import { Appointment as AppointmentType } from '@/types/appointment';
import { Patient as PatientType } from '@/types/patient';
import { Encounter } from '@/types/encounter';

interface AppointmentWithDetails extends AppointmentType {
  patientData?: PatientType;
  providerData?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface EncounterDisplayData {
  patientData: {
    firstName: string;
    lastName: string;
    patientId?: string;
    demographics?: {
      dateOfBirth?: string;
      gender?: 'male' | 'female' | 'other' | 'unknown';
      language?: string;
    };
    medicalRecord?: {
      allergies?: string[];
      conditions?: string[];
      medications?: string[];
    };
  };
  appointmentDetails: {
    scheduledDate: string;
    type?: string;
    location?: string;
    chiefComplaint?: string;
  };
  providerId: string;
  providerData: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const ALREADY_RECORDED_NOTICE = 'This visit already has a recording. Opening its note.';

const Recording: React.FC = () => {
  useCapabilities();
  const canRecord = useOrganizationStore((s) => s.canRecord);
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [searchParams] = useSearchParams();
  const encounterId = searchParams.get('encounterId');
  const { user, isReadOnly } = useAuth();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState<AppointmentWithDetails | null>(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Encounter mode state
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [encounterDisplayData, setEncounterDisplayData] = useState<EncounterDisplayData | null>(null);
  const isEncounterMode = !!encounterId;

  // Patient name for recovery UI and the upload progress panel. Build with per-part
  // fallbacks so a record missing first/last name degrades to undefined rather than the
  // literal "undefined undefined" (matches the header's defensive naming).
  const patientNameRaw = isEncounterMode
    ? `${encounterDisplayData?.patientData?.firstName || ''} ${encounterDisplayData?.patientData?.lastName || ''}`
    : `${appointment?.patientData?.firstName || ''} ${appointment?.patientData?.lastName || ''}`;
  const patientName = patientNameRaw.trim() || undefined;

  // Encounter/appointment date for the upload progress panel. Follow the same EHR
  // pre-localized convention as the header clock (read UTC components as-is for those
  // systems, otherwise localize en-US), drop EHR empty-date sentinels, and omit an
  // unparseable date rather than showing "Invalid Date".
  const scheduledDate = isEncounterMode
    ? encounterDisplayData?.appointmentDetails?.scheduledDate
    : appointment?.appointmentDetails?.scheduledDate;
  const externalSystemId = encounter?.externalSystemId;
  let encounterDate: string | undefined;
  let encounterTime: string | undefined;
  if (scheduledDate && !isSentinelDate(scheduledDate)) {
    const parsed = new Date(scheduledDate);
    if (!Number.isNaN(parsed.getTime())) {
      const preLocalized = !!externalSystemId && EHR_PRE_LOCALIZED_SYSTEMS.has(externalSystemId);
      encounterDate = preLocalized
        ? formatEhrDate(scheduledDate)
        : parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      encounterTime = preLocalized
        ? formatEhrTime(scheduledDate)
        : parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }

  const {
    isRecording,
    isPaused,
    recordingTime,
    audioUrl,
    isEncrypting,
    encryptedRecording,
    error,
    audioLevel,
    hasAudioInput,
    waveformData,
    savedChunksCount,
    lastChunkSavedAt,
    sessionId,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    flushChunks,
    isSupported
  } = useAudioRecorder(
    isEncounterMode ? '' : (appointmentId || ''),
    isEncounterMode ? '' : (appointment?.patientId || ''),
    user?.id || '',
    encounterId || undefined,
    patientName
  );

  // Recording protection hook for network/lifecycle monitoring
  const {
    isOnline,
    isAppActive,
    wasInterrupted,
    hasInterruptedSessions,
    interruptedSessionCount,
    initialize: initializeProtection,
    getInterruptedSessions,
    clearInterruptedSession,
  } = useRecordingProtection({
    onAppBackground: () => {
      if (isRecording && !isPaused) {
        // App went to background during recording - useAudioRecorder handles emergency flush
        // The useAudioRecorder hook handles emergency flush automatically
      }
    },
    onNetworkOffline: () => {
      if (isRecording) {
        toast('Network offline - recording will be saved locally', { icon: '📶' });
      }
    },
  });

  // Recovery modal state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoverySession, setRecoverySession] = useState<ReturnType<typeof getInterruptedSessions>[0] | null>(null);

  // Failed recordings indicator
  const [failedRecordingsCount, setFailedRecordingsCount] = useState(0);

  useEffect(() => {
    const checkFailedRecordings = async () => {
      try {
        const allRecordings = await recordingStorage.getAllRecordings();
        const failed = allRecordings.filter(
          r => r.uploadStatus === 'needs_attention' || r.uploadStatus === 'failed'
        );
        setFailedRecordingsCount(failed.length);
      } catch {
        // Ignore errors checking storage
      }
    };
    checkFailedRecordings();
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [transcriptionStatus, setTranscriptionStatus] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [maxRetryAttempts] = useState(3);

  const { enableDebugAudioUpload } = usePlatformFlags();
  const debugAudioUploadEnabled =
    enableDebugAudioUpload || import.meta.env.VITE_DEBUG_AUDIO_UPLOAD === 'true';

  // Visualization mode detection for AudioSphere
  const { mode: visualizationMode, reducedMotion } = useVisualizationMode();

  // Initialize protection services
  useEffect(() => {
    initializeProtection();
  }, [initializeProtection]);

  // Check for interrupted sessions on mount
  useEffect(() => {
    const sessions = getInterruptedSessions();
    if (sessions.length > 0 && !isRecording) {
      setRecoverySession(sessions[0]);
      setShowRecoveryModal(true);
    }
  }, [getInterruptedSessions, isRecording]);

  // Handle Android back button
  useEffect(() => {
    if (!PlatformService.isNative()) return;

    let backButtonHandler: { remove: () => Promise<void> } | null = null;

    const setupBackButton = async () => {
      backButtonHandler = await App.addListener('backButton', ({ canGoBack }) => {
        if (isRecording || isEncrypting) {
          setShowLeaveModal(true);
          return; // Prevent default back navigation
        }
        if (canGoBack) {
          window.history.back();
        } else {
          navigate('/dashboard');
        }
      });
    };

    setupBackButton();

    return () => {
      if (backButtonHandler) {
        backButtonHandler.remove();
      }
    };
  }, [isRecording, isEncrypting, navigate]);

  // PHASE 3: Enhanced transcription progress tracking
  const [transcriptionProgress, setTranscriptionProgress] = useState<{
    stage: string;
    progress: number;
    elapsedTime: number;
    estimatedTotalTime: number;
    processingSpeedRatio?: number;
  } | null>(null);
  const [showProgressUI, setShowProgressUI] = useState(false);

  // State for QuickRecording mode (when no IDs provided)
  const [showQuickRecordingModal, setShowQuickRecordingModal] = useState(false);

  // Cancel/Abort functionality
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showRerecordModal, setShowRerecordModal] = useState(false);

  // Track if transcription completed successfully (to allow redirect without blocking)
  const [isTranscriptionComplete, setIsTranscriptionComplete] = useState(false);

  // Subscription check state
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    canRecord: boolean;
    reason?: string;
    message?: string;
    checked: boolean;
  }>({ canRecord: true, checked: false });

  // The in-flight upload + transcription job lives in a global store so it survives
  // navigating away from this page. The page mirrors its own job for in-page display only.
  // Resolution is by page-local storage id when the bytes are still here (record flow), else
  // by encounter/appointment identity. A completed job is only adopted if this mount saw it
  // active (recorded below), so a job that finished while we were on another page is left to
  // the sidebar rather than auto-opened or shown mid-"Redirecting".
  const seenActiveJobKeysRef = useRef<Set<string>>(new Set());
  const currentJob = useUploadJobStore((s) =>
    selectPageJob(s.jobs, {
      storageId: encryptedRecording?.id ?? null,
      isEncounterMode,
      encounterId,
      appointmentId,
      seenActiveKeys: seenActiveJobKeysRef.current,
    })
  );

  // Mirror the store job into the existing local UI state so the in-page progress
  // UI keeps working while this page is mounted.
  useEffect(() => {
    if (!currentJob) {
      // The job left the store (terminal + auto-dismissed, or cancelled). Return the page to
      // its pre-upload state so the Upload control is available again instead of dead-ending
      // on a stale 'failed' status with no buttons.
      setIsUploading(false);
      setTranscriptionStatus(null);
      setTranscriptionProgress(null);
      return;
    }
    // Remember jobs this mount has seen mid-flight so selectPageJob only adopts a completed
    // job we actually watched finish here.
    if (currentJob.phase !== 'complete' && currentJob.phase !== 'failed' && currentJob.phase !== 'cancelled') {
      seenActiveJobKeysRef.current.add(currentJob.key);
    }
    const active = currentJob.phase === 'creating' || currentJob.phase === 'uploading';
    setIsUploading(active);
    setUploadProgress(currentJob.uploadProgress);
    setTranscriptionStatus(
      currentJob.phase === 'transcribing' ? 'processing'
        : currentJob.phase === 'complete' ? 'transcribed'
          : currentJob.phase === 'failed' ? 'failed'
            : null
    );
    setTranscriptionProgress(currentJob.transcription);
    if (currentJob.phase !== 'cancelled') {
      setShowProgressUI(true);
    }
    if (currentJob.phase === 'complete') {
      setIsTranscriptionComplete(true);
    }
  }, [currentJob]);

  // Parity with the previous inline flow: if this page's job completes while the clinician is
  // still here, redirect to review once. markNavigated runs inside the timeout (not in the
  // effect body) so patching the job does not re-run this effect and cancel its own timer; the
  // ref guard keeps it one-shot, and the timer is cleared only on unmount.
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (currentJob?.phase === 'complete' && !currentJob.navigated && navTimerRef.current === null) {
      const job = currentJob;
      toast.success('Transcription complete! Redirecting to review...');
      navTimerRef.current = setTimeout(() => {
        useUploadJobStore.getState().markNavigated(job.key);
        navigate(reviewPathFor(job));
      }, 2000);
    }
  }, [currentJob, navigate]);

  useEffect(() => () => { if (navTimerRef.current) clearTimeout(navTimerRef.current); }, []);

  // Navigation blocking - only block while audio is still being captured or encrypted,
  // because those bytes are page-local. Upload and transcription run in the global store.
  const shouldBlockNavigation = isRecording || isEncrypting;
  const blocker = useBlocker(shouldBlockNavigation);

  // Handle blocker state changes
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowLeaveModal(true);
    }
  }, [blocker.state]);

  // Browser beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlockNavigation]);

  // Load appointment or encounter data when component mounts
  useEffect(() => {
    if (isEncounterMode && encounterId) {
      loadEncounter();
    } else if (appointmentId) {
      loadAppointment();
    } else {
      setShowQuickRecordingModal(true);
      setAppointmentLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await apiService.checkCanRecord();
        if (response.success) {
          setSubscriptionStatus({
            canRecord: response.data.canRecord,
            reason: response.data.reason,
            message: response.data.message,
            checked: true
          });
        }
      } catch (error) {
        console.error('[Recording] Failed to check subscription status:', error);
        // On error, assume can record (backend will enforce)
        setSubscriptionStatus({ canRecord: true, checked: true });
      }
    };
    checkSubscription();
  }, []);

  const loadEncounter = async () => {
    if (!encounterId) {
      return;
    }

    setAppointmentLoading(true);
    setAppointmentError(null);
    setAccessDenied(false);

    try {
      const encounterData = await encounterService.getEncounterById(encounterId);
      setEncounter(encounterData);

      const displayData: EncounterDisplayData = {
        patientData: {
          firstName: encounterData.patientData.firstName,
          lastName: encounterData.patientData.lastName,
          patientId: encounterData.patientData.mrn,
          demographics: {
            // Drop EHR sentinel DOBs (e.g. 1899-12-31, 1900-01-01) so downstream
            // age calc and display don't show "126 years old" / "12/31/1899".
            dateOfBirth: encounterData.patientData.dateOfBirth && !isSentinelDate(encounterData.patientData.dateOfBirth)
              ? encounterData.patientData.dateOfBirth
              : undefined,
            gender: encounterData.patientData.gender as 'male' | 'female' | 'other' | 'unknown',
            language: 'en-US'
          },
          medicalRecord: {
            allergies: [],
            conditions: [],
            medications: []
          }
        },
        appointmentDetails: {
          scheduledDate: encounterData.encounterDetails.scheduledDate,
          type: encounterData.encounterDetails.type,
          location: encounterData.encounterDetails.location,
          chiefComplaint: encounterData.encounterDetails.chiefComplaint
        },
        providerId: encounterData.assignedUserId || user?.id || '',
        providerData: {
          id: encounterData.assignedUserId || user?.id || '',
          firstName: encounterData.encounterDetails.providerName?.split(' ')[0] || user?.firstName || 'Provider',
          lastName: encounterData.encounterDetails.providerName?.split(' ').slice(1).join(' ') || user?.lastName || ''
        }
      };

      setEncounterDisplayData(displayData);

      const userRole = (user as any)?.role;
      const isAdmin = userRole === 'admin' || userRole === 'superadmin';
      const isOperator = userRole === 'operator';

      const assignedId = typeof encounterData.assignedUserId === 'string'
        ? encounterData.assignedUserId
        : (encounterData.assignedUserId as any)?.id;

      const isAssignedOperator = assignedId === user?.id;
      const isUnassigned = !encounterData.assignedUserId;

      const hasAccess = isAdmin || (isOperator && (isAssignedOperator || isUnassigned));

      if (!hasAccess) {
        setAppointmentError('You are not authorized to record for this encounter');
        return;
      }

      // Checked once, on open: a recording made on this page finishes and opens its note as before.
      if (isAlreadyRecorded(encounterData)) {
        toast(ALREADY_RECORDED_NOTICE);
        navigate(reviewPathFor({ isEncounterMode: true, encounterId }), { replace: true });
        return;
      }
    } catch (error) {
      if (isRecordAccessDenied(error)) {
        setAccessDenied(true);
        return;
      }
      console.error('[Recording] Error loading encounter:', error);
      setAppointmentError(error instanceof Error ? error.message : 'Failed to load encounter');
    } finally {
      setAppointmentLoading(false);
    }
  };

  const loadAppointment = async () => {
    if (!appointmentId) return;

    setAppointmentLoading(true);
    setAppointmentError(null);
    setAccessDenied(false);

    try {
      const appointmentData = await appointmentWrapperService.getAppointment(appointmentId);

      let patientData;

      if (typeof appointmentData.patientId === 'object' && appointmentData.patientId !== null) {
        patientData = appointmentData.patientId as any;
      } else {
        const patientId = typeof appointmentData.patientId === 'string'
          ? appointmentData.patientId
          : (appointmentData.patientId as any).id || (appointmentData.patientId as any).toString();

        try {
          patientData = await appointmentWrapperService.getPatient(patientId);
        } catch (error) {
          patientData = {
          id: patientId,
          patientId: patientId.substring(0, 8),
          organizationId: '',
          firstName: 'Patient',
          lastName: patientId.substring(0, 6),
          demographics: {
            gender: 'unknown' as const,
            dateOfBirth: '',
            language: 'en-US'
          },
          medicalRecord: {
            allergies: [],
            conditions: [],
            medications: []
          },
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
          };
        }
      }

      let providerData = {
        id: appointmentData.providerId,
        firstName: user?.firstName || 'Provider',
        lastName: user?.lastName || ''
      };

      if (typeof appointmentData.providerId === 'object' && appointmentData.providerId !== null) {
        const providerId = appointmentData.providerId as any;
        providerData = {
          id: providerId.id || appointmentData.providerId,
          firstName: providerId.firstName || user?.firstName || 'Provider',
          lastName: providerId.lastName || user?.lastName || ''
        };
      }

      const fullAppointmentData: AppointmentWithDetails = {
        ...appointmentData,
        patientId: typeof appointmentData.patientId === 'object'
          ? (appointmentData.patientId as any).id
          : appointmentData.patientId,
        patientData: {
          ...patientData,
          patientId: patientData.patientId || patientData.id?.substring(0, 8),
        } as PatientType,
        providerData
      };

      setAppointment(fullAppointmentData);

      const userRole = (user as any)?.role;
      const isAdmin = userRole === 'admin' || userRole === 'superadmin';
      const isOperator = userRole === 'operator';
      const providerId = typeof appointmentData.providerId === 'string'
        ? appointmentData.providerId
        : (appointmentData.providerId as any)?.id;
      const isAssignedProvider = providerId === user?.id;
      const isUnassigned = !appointmentData.providerId;

      const hasAccess = isAdmin || (isOperator && (isAssignedProvider || isUnassigned));

      if (!hasAccess) {
        setAppointmentError('You are not authorized to record for this appointment');
        return;
      }

      if (isAlreadyRecorded(appointmentData)) {
        toast(ALREADY_RECORDED_NOTICE);
        navigate(reviewPathFor({ isEncounterMode: false, appointmentId }), { replace: true });
        return;
      }
    } catch (error) {
      if (isRecordAccessDenied(error)) {
        setAccessDenied(true);
        return;
      }
      console.error('[Recording] Error loading appointment:', error);
      setAppointmentError(error instanceof Error ? error.message : 'Failed to load appointment');
    } finally {
      setAppointmentLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    const audio = document.getElementById('audioPlayer') as HTMLAudioElement;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Cancel processing handler
  const handleCancelProcessing = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    // Abort the in-flight upload/transcription job (aborts the request via its signal)
    if (currentJob) {
      useUploadJobStore.getState().cancel(currentJob.key);
    }

    // Stop recording if active
    if (isRecording) {
      stopRecording();
    }

    // Reset all state
    resetRecording();
    setIsUploading(false);
    setUploadProgress(0);
    setTranscriptionStatus(null);
    setTranscriptionProgress(null);
    setShowProgressUI(false);
    setShowCancelModal(false);

    toast.success('Recording cancelled');
  };

  const confirmRerecord = async () => {
    await resetRecording();
    setShowRerecordModal(false);
    toast.success('Recording discarded. Ready to re-record.');
  };

  // Handle leave page confirmation
  const confirmLeave = () => {
    if (isRecording) {
      stopRecording();
    }
    resetRecording();
    setShowLeaveModal(false);
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const cancelLeave = () => {
    setShowLeaveModal(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleUpload = async () => {
    if (!encryptedRecording) {
      toast.error('No recording available to upload');
      return;
    }

    // Guard against a duplicate upload of the same recording (e.g. double-click).
    if (currentJob && (currentJob.phase === 'creating' || currentJob.phase === 'uploading' || currentJob.phase === 'transcribing')) {
      toast('Upload already in progress', { icon: 'ℹ️' });
      return;
    }

    setShowProgressUI(true);

    // Hand the upload + transcription poll to the global store so it survives navigation.
    // Progress, completion, errors, and IndexedDB cleanup are owned there and surfaced
    // by the persistent upload toast.
    await useUploadJobStore.getState().runUpload({
      encryptedRecording,
      recordingTime,
      isEncounterMode,
      appointmentId: isEncounterMode ? undefined : appointmentId,
      encounterId: isEncounterMode ? (encounterId || undefined) : undefined,
      patientName,
      encounterDate,
      encounterTime,
    });
  };

  // Mark the appointment as being recorded, or a scheduled encounter as in progress, once capture
  // has really started (microphone granted, recorder running), not on the click: a denied
  // microphone left visits stuck as "recording". Best effort: capture never waits on it, and the
  // upload sets the encounter in progress anyway.
  const markOnCaptureRef = useRef(false);
  const handleStartRecording = () => {
    markOnCaptureRef.current = isEncounterMode ? !!encounterId : !!appointmentId;
    startRecording();
  };
  useEffect(() => {
    if (!isRecording || !markOnCaptureRef.current) return;
    markOnCaptureRef.current = false;
    if (isEncounterMode && encounterId) {
      if ((encounter?.encounterDetails?.status ?? 'scheduled') !== 'scheduled') return;
      encounterService.updateEncounterStatus(encounterId, 'in-progress')
        .then(() => {
          setEncounter((prev) =>
            prev ? { ...prev, encounterDetails: { ...prev.encounterDetails, status: 'in-progress' } } : prev
          );
        })
        .catch(() => {});
    } else if (appointmentId) {
      appointmentService.startRecording(appointmentId).catch(() => {});
    }
  }, [isRecording, isEncounterMode, encounterId, appointmentId, encounter?.encounterDetails?.status]);

  // AMP-136: only users who may record get the recorder (the server refuses the others anyway).
  // Never while a capture is running or its audio is waiting to upload: if the answer arrives
  // mid-recording, keep the controls so the user can stop, save and upload (the upload is then
  // refused and the audio kept on the device as needs_attention).
  if (canRecord === false && !isRecording && !isEncrypting && !encryptedRecording) {
    return <RecordingNotAllowed />;
  }

  // Quick recording modal handling for no appointment/encounter
  if (!appointmentId && !encounterId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl font-light tracking-tight text-slate-800">
              Clinical Recording
            </h1>
          </motion.div>

          <QuickRecordingModal
            isOpen={showQuickRecordingModal}
            onClose={() => {
              setShowQuickRecordingModal(false);
              navigate('/dashboard');
            }}
          />
        </div>
      </div>
    );
  }

  // Loading state
  if (appointmentLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4">
            <svg className="animate-spin text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <p className="text-slate-600 font-light">
            Loading {isEncounterMode ? 'encounter' : 'appointment'}...
          </p>
        </motion.div>
      </div>
    );
  }

  if (accessDenied) {
    return <RecordAccessDenied backTo={isEncounterMode ? '/encounters' : '/appointments'} />;
  }

  // Determine if we have the required data
  const hasData = isEncounterMode ? !!encounterDisplayData : !!appointment;

  // Error state
  if (appointmentError || !hasData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          </div>
          <h3 className="text-xl font-medium text-slate-800 mb-2">
            {isEncounterMode ? 'Unable to load encounter' : 'Unable to load appointment'}
          </h3>
          <p className="text-slate-500 mb-6">
            {appointmentError || (isEncounterMode ? 'Encounter not found' : 'Appointment not found')}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(isEncounterMode ? '/dashboard' : '/appointments')}
              className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors"
            >
              Dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-50 flex items-center justify-center">
            <MicrophoneIcon className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="text-xl font-medium text-slate-800 mb-2">
            Audio recording not supported
          </h3>
          <p className="text-slate-500">
            Your browser doesn't support audio recording. Please use Chrome, Firefox, or Safari.
          </p>
        </motion.div>
      </div>
    );
  }

  // Determine current sphere state
  const getSphereState = () => {
    if (isUploading) return 'uploading';
    if (isEncrypting) return 'encrypting';
    if (encryptedRecording) return 'complete';
    if (isRecording && isPaused) return 'paused';
    if (isRecording) return 'recording';
    return 'idle';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pb-[env(safe-area-inset-bottom)]">
      {/* Cancel Confirmation Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                Cancel Recording?
              </h3>
              <p className="text-slate-500 text-center text-sm mb-6">
                This will discard your recording and stop all processing. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Keep Recording
                </button>
                <button
                  onClick={confirmCancel}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
                >
                  Cancel & Discard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leave Page Confirmation Modal */}
      <AnimatePresence>
        {showLeaveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                Leave Page?
              </h3>
              <p className="text-slate-500 text-center text-sm mb-6">
                You have an active recording. Leaving this page will stop the recording and discard all data.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelLeave}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Stay
                </button>
                <button
                  onClick={confirmLeave}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
                >
                  Leave & Discard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Re-record Confirmation Modal */}
      <AnimatePresence>
        {showRerecordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowRerecordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                Discard Recording?
              </h3>
              <p className="text-slate-500 text-center text-sm mb-6">
                This will permanently discard the current recording. You will need to record again from the beginning.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRerecordModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Keep Recording
                </button>
                <button
                  onClick={confirmRerecord}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
                >
                  Discard & Re-record
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recording Recovery Modal */}
      {recoverySession && (
        <RecordingRecoveryModal
          isOpen={showRecoveryModal}
          session={recoverySession}
          onResume={() => {
            // TODO: Implement actual recovery logic
            // For now, just close the modal and navigate to recording
            setShowRecoveryModal(false);
            clearInterruptedSession(recoverySession.sessionId);
            toast.success('Session cleared - start a new recording');
          }}
          onDiscard={() => {
            clearInterruptedSession(recoverySession.sessionId);
            // Clean up orphaned chunks from IndexedDB
            import('@/services/chunkStorage.service').then(({ chunkStorageService }) => {
              chunkStorageService.deleteSessionChunks(recoverySession.sessionId);
            });
            setShowRecoveryModal(false);
            setRecoverySession(null);
            toast.success('Interrupted recording discarded');
          }}
          onClose={() => {
            setShowRecoveryModal(false);
          }}
        />
      )}

      <div className={`max-w-3xl mx-auto px-4 ${isRecording ? 'py-3 space-y-3' : 'py-8 space-y-6'}`}>
        {/* Protection Status Bar */}
        {(isRecording || savedChunksCount > 0) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between px-4 py-2 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200/50"
          >
            <div className="flex items-center gap-3">
              <NetworkStatusBadge
                isOnline={isOnline}
                savedChunksCount={savedChunksCount}
                lastSavedAt={lastChunkSavedAt}
                isRecording={isRecording}
                compact={false}
              />
            </div>
            {savedChunksCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                <ShieldCheckIcon className="w-4 h-4" />
                <span>Auto-protected</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Failed Recordings Warning Banner */}
        {failedRecordingsCount > 0 && !isRecording && !encryptedRecording && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span className="text-sm text-amber-700">
                {failedRecordingsCount} recording{failedRecordingsCount > 1 ? 's' : ''} pending upload
              </span>
            </div>
            <button
              onClick={async () => {
                const { uploadRetryWorker } = await import('@/services/uploadRetryWorker');
                const count = await uploadRetryWorker.retryAllFailed();
                if (count > 0) {
                  toast.success(`Retrying ${count} recording${count > 1 ? 's' : ''}...`);
                  setFailedRecordingsCount(0);
                }
              }}
              className="text-sm font-medium text-amber-700 hover:text-amber-800 underline flex-shrink-0"
            >
              Retry
            </button>
          </motion.div>
        )}

        {/* Patient Info Card */}
        <PatientCockpitChip
          appointment={isEncounterMode && encounterDisplayData ? encounterDisplayData as any : appointment}
          isEncounterMode={isEncounterMode}
          isCompact={isRecording}
          externalSystem={encounter?.externalSystemId}
        />

        {/* Debug Audio Uploader — hands off to the background upload job; the progress toast
            (and its completion / retry) is owned by UploadJobController, so no completion
            callback here (the old one fired at hand-off and read as a false "completed"). */}
        {debugAudioUploadEnabled && hasData && (
          <DebugAudioUploader
            {...(isEncounterMode
              ? { encounterId: encounterId!, patientId: encounter?.patientData?.mrn || '' }
              : { appointmentId: appointment!.id, patientId: appointment!.patientId }
            )}
            patientName={patientName}
            encounterDate={encounterDate}
            encounterTime={encounterTime}
          />
        )}

        {/* Main Recording Interface */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative"
        >
          {/* Glass Card */}
          <div className="backdrop-blur-xl bg-white/80 rounded-3xl border border-white/40 shadow-xl overflow-hidden">
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-indigo-50/20 pointer-events-none" />

            <div className={`relative ${isRecording ? 'p-4 sm:p-6 md:p-8' : 'p-8'}`}>
              {/* Timer - Always visible when recording */}
              <AnimatePresence>
                {(isRecording || recordingTime > 0) && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`text-center ${isRecording ? 'mb-3' : 'mb-6'}`}
                  >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/5 rounded-full">
                      {isRecording && !isPaused && (
                        <motion.div
                          className="w-2 h-2 bg-red-500 rounded-full"
                          animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        />
                      )}
                      <span className="text-2xl font-mono font-medium text-slate-800 tracking-wider">
                        {formatTime(recordingTime)}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Hero AudioSphere */}
              <div className={`flex justify-center ${isRecording ? 'mb-4' : 'mb-8'}`}>
                <div className={`relative transition-all duration-500 ${
                  isRecording
                    ? 'w-44 h-44 sm:w-56 sm:h-56 md:w-72 md:h-72'
                    : 'w-72 h-72 sm:w-80 sm:h-80'
                }`}>
                  {visualizationMode === '3d' ? (
                    <AudioSphere
                      audioLevel={audioLevel}
                      state={getSphereState()}
                      className="w-full h-full"
                      progress={
                        (isUploading || transcriptionStatus === 'processing')
                          ? {
                              stage: transcriptionProgress?.stage || 'securing',
                              percent: transcriptionProgress?.progress || uploadProgress || 0
                            }
                          : null
                      }
                    />
                  ) : (
                    <AudioSphere2D
                      audioLevel={audioLevel}
                      state={getSphereState()}
                      className="w-full h-full"
                      reducedMotion={reducedMotion}
                      progress={
                        (isUploading || transcriptionStatus === 'processing')
                          ? {
                              stage: transcriptionProgress?.stage || 'securing',
                              percent: transcriptionProgress?.progress || uploadProgress || 0
                            }
                          : null
                      }
                    />
                  )}

                  {/* Progress Ring Overlay for Upload */}
                  {isUploading && (
                    <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="46"
                        fill="none"
                        stroke="rgba(99, 102, 241, 0.1)"
                        strokeWidth="2"
                      />
                      <motion.circle
                        cx="50"
                        cy="50"
                        r="46"
                        fill="none"
                        stroke="rgba(99, 102, 241, 0.8)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 46}`}
                        strokeDashoffset={2 * Math.PI * 46 * (1 - uploadProgress / 100)}
                        initial={{ strokeDashoffset: 2 * Math.PI * 46 }}
                        animate={{ strokeDashoffset: 2 * Math.PI * 46 * (1 - uploadProgress / 100) }}
                        transition={{ duration: 0.3 }}
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Status Text & Progress Pipeline */}
              <div className="text-center mb-6">
                {/* Show detailed progress pipeline during upload/transcription */}
                {(isUploading || transcriptionStatus === 'processing' || transcriptionStatus === 'transcribed') ? (
                  <div className="space-y-4">
                    {/* Current Stage Title */}
                    <h2 className="text-xl font-medium text-slate-800">
                      {transcriptionStatus === 'transcribed' ? 'Complete!' :
                       transcriptionProgress?.stage === 'complete' ? 'Complete!' :
                       transcriptionProgress?.stage === 'transcribing' ? 'Transcribing...' :
                       transcriptionProgress?.stage === 'enhancing' ? 'Enhancing...' :
                       transcriptionProgress?.stage === 'finalizing' ? 'Finalizing...' :
                       transcriptionProgress?.stage === 'decrypting' ? 'Decrypting...' :
                       transcriptionProgress?.stage === 'converting' ? 'Converting...' :
                       isUploading && uploadProgress < 100 ? 'Uploading...' :
                       'Processing...'}
                    </h2>

                    {/* Progress Pipeline Steps */}
                    <div className="flex items-center justify-center gap-1 px-4">
                      {[
                        { key: 'upload', label: 'Upload', icon: '↑' },
                        { key: 'decrypt', label: 'Decrypt', icon: '🔓' },
                        { key: 'convert', label: 'Convert', icon: '⚙' },
                        { key: 'transcribe', label: 'Transcribe', icon: '📝' },
                        { key: 'enhance', label: 'Enhance', icon: '✨' },
                        { key: 'complete', label: 'Done', icon: '✓' }
                      ].map((step, index) => {
                        // Determine step status based on current progress
                        const currentStage = transcriptionProgress?.stage || (isUploading ? 'securing' : 'upload');
                        const stageOrder = ['securing', 'upload', 'decrypting', 'converting', 'transcribing', 'enhancing', 'finalizing', 'complete'];
                        const stepMapping: Record<string, number> = {
                          'upload': 1,
                          'decrypt': 2,
                          'convert': 3,
                          'transcribe': 4,
                          'enhance': 5,
                          'complete': 7
                        };
                        const currentIndex = stageOrder.indexOf(currentStage);
                        const stepIndex = stepMapping[step.key];

                        const isComplete = currentIndex >= stepIndex || transcriptionStatus === 'transcribed';
                        const isActive = currentIndex === stepIndex - 1 || (step.key === 'upload' && isUploading && uploadProgress < 100);

                        return (
                          <div key={step.key} className="flex items-center">
                            <motion.div
                              className={`
                                flex flex-col items-center min-w-[48px]
                              `}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <motion.div
                                className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300
                                  ${isComplete
                                    ? 'bg-emerald-500 text-white'
                                    : isActive
                                      ? 'bg-blue-500 text-white ring-4 ring-blue-500/20'
                                      : 'bg-slate-100 text-slate-400'
                                  }
                                `}
                                animate={isActive ? { scale: [1, 1.1, 1] } : {}}
                                transition={{ repeat: isActive ? Infinity : 0, duration: 1.5 }}
                              >
                                {isComplete ? '✓' : step.icon}
                              </motion.div>
                              <span className={`
                                text-[10px] mt-1 font-medium transition-colors duration-300
                                ${isComplete ? 'text-emerald-600' : isActive ? 'text-blue-600' : 'text-slate-400'}
                              `}>
                                {step.label}
                              </span>
                            </motion.div>
                            {index < 5 && (
                              <div className={`
                                w-4 h-0.5 mx-0.5 transition-colors duration-300 -mt-4
                                ${currentIndex > stepIndex ? 'bg-emerald-400' : 'bg-slate-200'}
                              `} />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Progress percentage and time */}
                    <div className="text-sm text-slate-500">
                      {transcriptionStatus === 'transcribed' ? (
                        <span className="text-emerald-600 font-medium">Redirecting to review...</span>
                      ) : (
                        <div className="flex items-center justify-center gap-3">
                          <span>{Math.round(transcriptionProgress?.progress || uploadProgress || 0)}% complete</span>
                          {transcriptionProgress?.elapsedTime && transcriptionProgress.elapsedTime > 0 && (
                            <span className="text-slate-400">•</span>
                          )}
                          {transcriptionProgress?.elapsedTime && transcriptionProgress.elapsedTime > 0 && (
                            <span>{Math.round(transcriptionProgress.elapsedTime)}s elapsed</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Default status display for other states */
                  <>
                    <h2 className="text-xl font-medium text-slate-800 mb-1">
                      {isEncrypting ? 'Encrypting...' :
                       encryptedRecording ? 'Ready to Upload' :
                       isRecording ? (isPaused ? 'Paused' : 'Recording') :
                       'Ready'}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {isEncrypting ? 'AES-256 encryption in progress' :
                       encryptedRecording ? `${formatTime(recordingTime)} recorded & encrypted` :
                       isRecording ? (isPaused ? 'Tap resume to continue' : (hasAudioInput ? 'Audio detected' : 'Listening...')) :
                       'Tap to start recording'}
                    </p>
                  </>
                )}
              </div>

              {/* Controls */}
              <div className="flex flex-wrap justify-center gap-3">
                {/* Idle State - Start Button (hidden while a rehydrated job is still uploading/transcribing) */}
                {/* canRecord !== null: wait for who may record (AMP-136) before offering the microphone */}
                {!isRecording && !isEncrypting && !encryptedRecording && !isUploading && !transcriptionStatus && !isReadOnly() && subscriptionStatus.canRecord && canRecord !== null && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleStartRecording}
                    className="px-8 py-3.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-shadow flex items-center gap-2"
                  >
                    <MicrophoneIcon className="w-5 h-5" />
                    <span>Start Recording</span>
                  </motion.button>
                )}

                {/* Recording State - Pause/Resume + Stop */}
                {isRecording && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={isPaused ? resumeRecording : pauseRecording}
                      className="px-6 py-3 bg-white border border-slate-200 text-slate-700 font-medium rounded-2xl hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      {isPaused ? <PlayIcon className="w-5 h-5" /> : <PauseIcon className="w-5 h-5" />}
                      <span>{isPaused ? 'Resume' : 'Pause'}</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={stopRecording}
                      className="px-6 py-3 bg-red-500 text-white font-medium rounded-2xl hover:bg-red-600 transition-colors flex items-center gap-2"
                    >
                      <StopIcon className="w-5 h-5" />
                      <span>Stop</span>
                    </motion.button>
                  </>
                )}

                {/* Complete State - Re-record, Preview & Upload */}
                {encryptedRecording && !isEncrypting && !isUploading && !transcriptionStatus && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowRerecordModal(true)}
                      className="px-5 py-3 bg-white border border-slate-200 text-slate-500 font-medium rounded-2xl hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <ArrowPathIcon className="w-5 h-5" />
                      <span>Re-record</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handlePlayPause}
                      disabled={!audioUrl}
                      className="px-5 py-3 bg-white border border-slate-200 text-slate-700 font-medium rounded-2xl hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                      <span>{isPlaying ? 'Pause' : 'Preview'}</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleUpload}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-shadow flex items-center gap-2"
                    >
                      <CloudArrowUpIcon className="w-5 h-5" />
                      <span>Upload & Transcribe</span>
                    </motion.button>
                  </>
                )}

                {/* Cancel Button - During Upload/Transcription */}
                {(isUploading || transcriptionStatus === 'processing') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCancelProcessing}
                    className="px-6 py-3 bg-white border border-slate-200 text-slate-600 font-medium rounded-2xl hover:bg-slate-50 transition-colors flex items-center gap-2"
                  >
                    <XMarkIcon className="w-5 h-5" />
                    <span>Cancel</span>
                  </motion.button>
                )}

                {/* Failed State - offer retry so the page does not dead-end after a failure */}
                {transcriptionStatus === 'failed' && encryptedRecording && !isUploading && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowRerecordModal(true)}
                      className="px-5 py-3 bg-white border border-slate-200 text-slate-500 font-medium rounded-2xl hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <ArrowPathIcon className="w-5 h-5" />
                      <span>Re-record</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        // Clear the failed job (and its toast) before re-dispatching, so the
                        // retry starts clean instead of racing the stale failed entry.
                        if (currentJob) useUploadJobStore.getState().dismiss(currentJob.key);
                        void handleUpload();
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-shadow flex items-center gap-2"
                    >
                      <CloudArrowUpIcon className="w-5 h-5" />
                      <span>Try Again</span>
                    </motion.button>
                  </>
                )}

                {/* Read-only message (role-based) */}
                {!isRecording && !encryptedRecording && isReadOnly() && (
                  <div className="text-center">
                    <p className="text-sm text-amber-600 bg-amber-50 px-4 py-2 rounded-xl">
                      Read-only access. Recording is restricted to operators.
                    </p>
                  </div>
                )}

                {/* Subscription blocked message */}
                {!isRecording && !encryptedRecording && !isReadOnly() && subscriptionStatus.checked && !subscriptionStatus.canRecord && (
                  <div className="text-center max-w-md">
                    <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl">
                      <p className="text-sm text-amber-700 font-medium">
                        {subscriptionStatus.message || 'Your organization does not have an active subscription.'}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Please contact your administrator.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-red-700">
                    <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                </motion.div>
              )}

              {/* Hidden Audio Player */}
              {audioUrl && (
                <audio id="audioPlayer" src={audioUrl} className="hidden" />
              )}
            </div>
          </div>

          {/* Recording Tip - Single Subtle Line */}
          <div className="mt-4 text-center">
            <p className="text-xs text-slate-400">
              Speak clearly in a quiet environment • Keep microphone 6-12 inches away
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Patient Cockpit Chip Component
interface PatientCockpitChipProps {
  appointment: AppointmentWithDetails | EncounterDisplayData | null;
  isEncounterMode?: boolean;
  isCompact?: boolean;
  externalSystem?: string;
}

const PatientCockpitChip: React.FC<PatientCockpitChipProps> = ({ appointment, isEncounterMode = false, isCompact = false, externalSystem }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!appointment) return null;

  const getPatientAge = () => {
    const dob = appointment.patientData?.demographics?.dateOfBirth;
    if (!dob || isSentinelDate(dob)) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getGenderAbbrev = () => {
    const gender = appointment.patientData?.demographics?.gender;
    switch (gender) {
      case 'male': return 'M';
      case 'female': return 'F';
      case 'other': return 'O';
      default: return null;
    }
  };

  const getDisplayString = () => {
    const name = `${appointment.patientData?.firstName || 'Patient'} ${appointment.patientData?.lastName || ''}`.trim();
    const age = getPatientAge();
    const gender = getGenderAbbrev();
    const ageGender = [age, gender].filter(Boolean).join('');
    const chiefComplaint = appointment.appointmentDetails?.chiefComplaint;
    const appointmentType = appointment.appointmentDetails?.type;

    const parts = [name];
    if (ageGender) parts.push(ageGender);
    if (chiefComplaint) parts.push(chiefComplaint);
    else if (appointmentType) parts.push(`${appointmentType} visit`);

    return parts.join(' • ');
  };

  const hasCriticalAlerts = () => {
    return (appointment.patientData?.medicalRecord?.allergies?.length || 0) > 0;
  };

  // Read UTC components as-is for EHR pre-localized systems, otherwise localize en-US. Date and
  // time are shown or hidden together; drop EHR sentinels and unparseable dates.
  const scheduledDate = appointment.appointmentDetails?.scheduledDate;
  const preLocalized = !!externalSystem && EHR_PRE_LOCALIZED_SYSTEMS.has(externalSystem);
  const getScheduledWhen = () => {
    if (!scheduledDate || isSentinelDate(scheduledDate)) return null;
    const parsed = new Date(scheduledDate);
    if (Number.isNaN(parsed.getTime())) return null;
    const date = preLocalized
      ? formatEhrDate(scheduledDate)
      : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = preLocalized
      ? formatEhrTime(scheduledDate)
      : parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} · ${time}`;
  };
  const scheduledWhen = getScheduledWhen();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className={`
          backdrop-blur-xl rounded-2xl border shadow-lg cursor-pointer transition-all duration-300
          ${isExpanded
            ? 'bg-white/90 border-blue-200/50 shadow-xl'
            : 'bg-white/70 border-white/40 hover:bg-white/80 hover:border-slate-200/50'
          }
        `}
        onClick={() => !isCompact && setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.005 }}
        whileTap={{ scale: 0.995 }}
      >
        {/* Compact Header */}
        <div className={`flex items-center justify-between ${isCompact ? 'p-2.5' : 'p-4'}`}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`
              ${isCompact ? 'w-8 h-8' : 'w-11 h-11'} rounded-xl flex items-center justify-center transition-all duration-300 relative flex-shrink-0
              ${hasCriticalAlerts()
                ? 'bg-gradient-to-br from-orange-400 to-red-500'
                : 'bg-gradient-to-br from-blue-400 to-indigo-500'
              }
            `}>
              <UserIcon className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} text-white`} />
              {hasCriticalAlerts() && !isCompact && (
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              )}
            </div>

            <div className="min-w-0 flex-1">
              {isCompact ? (
                <p className="font-medium text-slate-800 truncate text-sm">
                  {`${appointment.patientData?.firstName || 'Patient'} ${appointment.patientData?.lastName || ''}`.trim()}
                </p>
              ) : (
                <>
                  <p className="font-medium text-slate-800 truncate">
                    {`${appointment.patientData?.firstName || 'Patient'} ${appointment.patientData?.lastName || ''}`.trim()}
                    {(getPatientAge() || getGenderAbbrev()) && (
                      <span className="text-slate-400 ml-1.5 font-normal">
                        {[getPatientAge(), getGenderAbbrev()].filter(Boolean).join('')}
                      </span>
                    )}
                  </p>
                  {(appointment.appointmentDetails?.chiefComplaint || appointment.appointmentDetails?.type) && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {appointment.appointmentDetails?.chiefComplaint || `${appointment.appointmentDetails?.type} visit`}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-0.5">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      <span>HIPAA Secure</span>
                    </div>
                    {isEncounterMode && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        <span className="font-medium">Connected</span>
                      </div>
                    )}
                    {scheduledWhen && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <ClockIcon className="w-3 h-3" />
                        <span>{scheduledWhen}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {!isCompact && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="text-slate-400 ml-2"
            >
              <ChevronDownIcon className="w-5 h-5" />
            </motion.div>
          )}
        </div>

        {/* Expandable Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-slate-100 overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Demographics Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                      Patient
                    </h4>
                    <div className="text-sm text-slate-700 space-y-1">
                      <p><span className="text-slate-500">ID:</span> {appointment.patientData?.patientId || 'N/A'}</p>
                      <p>
                        <span className="text-slate-500">DOB:</span>{' '}
                        {(() => {
                          const dob = appointment.patientData?.demographics?.dateOfBirth;
                          if (!dob || isSentinelDate(dob)) return 'Not Available';
                          const d = new Date(dob);
                          return isNaN(d.getTime()) ? 'Not Available' : d.toLocaleDateString();
                        })()}
                      </p>
                      {appointment.patientData?.demographics?.gender && (
                        <p><span className="text-slate-500">Gender:</span> {appointment.patientData.demographics.gender}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                      Provider
                    </h4>
                    <p className="text-sm text-slate-700">
                      Dr. {appointment.providerData?.firstName} {appointment.providerData?.lastName}
                    </p>
                    {appointment.appointmentDetails?.location && (
                      <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                        <MapPinIcon className="w-3.5 h-3.5" />
                        <span>{appointment.appointmentDetails.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Medical Safety */}
                {((appointment.patientData?.medicalRecord?.allergies?.length || 0) > 0 ||
                  (appointment.patientData?.medicalRecord?.conditions?.length || 0) > 0 ||
                  (appointment.patientData?.medicalRecord?.medications?.length || 0) > 0) && (
                  <div className="pt-3 border-t border-slate-100">
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                      Medical Safety
                    </h4>
                    <div className="space-y-2">
                      {(appointment.patientData?.medicalRecord?.allergies?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {appointment.patientData?.medicalRecord?.allergies?.map((allergy, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-medium">
                              ⚠️ {allergy}
                            </span>
                          ))}
                        </div>
                      )}

                      {(appointment.patientData?.medicalRecord?.conditions?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {appointment.patientData?.medicalRecord?.conditions?.slice(0, 4).map((condition, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs">
                              {condition}
                            </span>
                          ))}
                          {(appointment.patientData?.medicalRecord?.conditions?.length || 0) > 4 && (
                            <span className="text-xs text-slate-400">+{(appointment.patientData?.medicalRecord?.conditions?.length || 0) - 4} more</span>
                          )}
                        </div>
                      )}

                      {(appointment.patientData?.medicalRecord?.medications?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {appointment.patientData?.medicalRecord?.medications?.slice(0, 4).map((medication, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs">
                              {medication}
                            </span>
                          ))}
                          {(appointment.patientData?.medicalRecord?.medications?.length || 0) > 4 && (
                            <span className="text-xs text-slate-400">+{(appointment.patientData?.medicalRecord?.medications?.length || 0) - 4} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default Recording;
